import { useState, useEffect, ReactNode } from 'react'
import { OnboardingState, OnboardingStep, OnboardingContext, ColumnInfo, UseCaseSuggestion } from './OnboardingContext'
import { useAuth } from '@/contexts/AuthContext'
import { usersService } from '@/services/users'

// Base keys - will be prefixed with user ID for user-specific storage
const STORAGE_KEY_BASE = 'brazilnut_onboarding_state'
const COMPLETED_KEY_BASE = 'brazilnut_onboarding_completed'
const SKIPPED_KEY_BASE = 'brazilnut_onboarding_skipped'

// Helper to get user-scoped storage keys
function getUserStorageKey(baseKey: string, userId: string | null): string {
  if (!userId) return baseKey
  return `${baseKey}_${userId}`
}

const INITIAL_STATE: OnboardingState = {
  currentStep: 1,
  isCompleted: false,
  dataSourceId: null,
  modelId: null,
  predictionId: null,
  useCase: null,
  modelName: '',
  columnInfo: null,
  suggestedUseCases: null,
  suggestedTarget: null,
}

function loadStateFromStorage(userId: string | null): OnboardingState {
  try {
    const key = getUserStorageKey(STORAGE_KEY_BASE, userId)
    const stored = localStorage.getItem(key)
    if (stored) {
      const parsed = JSON.parse(stored)
      return { ...INITIAL_STATE, ...parsed }
    }
  } catch (error) {
    console.error('Failed to load onboarding state:', error)
  }
  return INITIAL_STATE
}

function saveStateToStorage(state: OnboardingState, userId: string | null) {
  try {
    const key = getUserStorageKey(STORAGE_KEY_BASE, userId)
    localStorage.setItem(key, JSON.stringify(state))
  } catch (error) {
    console.error('Failed to save onboarding state:', error)
  }
}

export function useOnboarding() {
  const { user } = useAuth()
  const userId = user?.id || null

  const [state, setState] = useState<OnboardingState>(() => loadStateFromStorage(userId))

  // Re-load state when user changes
  useEffect(() => {
    setState(loadStateFromStorage(userId))
  }, [userId])

  // Persist state changes to localStorage
  useEffect(() => {
    if (userId) {
      saveStateToStorage(state, userId)
    }
  }, [state, userId])

  const setCurrentStep = (step: OnboardingStep) => {
    setState((prev) => ({ ...prev, currentStep: step }))
  }

  const setDataSourceId = (id: string | null) => {
    setState((prev) => ({ ...prev, dataSourceId: id }))
  }

  const setModelId = (id: string | null) => {
    setState((prev) => ({ ...prev, modelId: id }))
  }

  const setPredictionId = (id: string | null) => {
    setState((prev) => ({ ...prev, predictionId: id }))
  }

  const setUseCase = (useCase: string | null) => {
    setState((prev) => ({ ...prev, useCase }))
  }

  const setModelName = (modelName: string) => {
    setState((prev) => ({ ...prev, modelName }))
  }

  const setColumnInfo = (info: ColumnInfo[] | null) => {
    setState((prev) => ({ ...prev, columnInfo: info }))
  }

  const setSuggestedUseCases = (suggestions: UseCaseSuggestion[] | null) => {
    setState((prev) => ({ ...prev, suggestedUseCases: suggestions }))
  }

  const setSuggestedTarget = (target: string | null) => {
    setState((prev) => ({ ...prev, suggestedTarget: target }))
  }

  const completeOnboarding = async () => {
    setState((prev) => ({ ...prev, isCompleted: true }))
    await markOnboardingComplete(userId)
  }

  const resetOnboarding = () => {
    setState(INITIAL_STATE)
    const stateKey = getUserStorageKey(STORAGE_KEY_BASE, userId)
    const completedKey = getUserStorageKey(COMPLETED_KEY_BASE, userId)
    const skippedKey = getUserStorageKey(SKIPPED_KEY_BASE, userId)
    localStorage.removeItem(stateKey)
    localStorage.removeItem(completedKey)
    localStorage.removeItem(skippedKey)
  }

  const skipOnboarding = async () => {
    const skippedKey = getUserStorageKey(SKIPPED_KEY_BASE, userId)
    localStorage.setItem(skippedKey, 'true')
    await markOnboardingComplete(userId)
  }

  return {
    state,
    setCurrentStep,
    setDataSourceId,
    setModelId,
    setPredictionId,
    setUseCase,
    setModelName,
    setColumnInfo,
    setSuggestedUseCases,
    setSuggestedTarget,
    completeOnboarding,
    resetOnboarding,
    skipOnboarding,
  }
}

// Hook to check if user has completed or skipped onboarding
export function useHasCompletedOnboarding(): boolean {
  const { user, isLoading } = useAuth()
  const userId = user?.id || null

  const [hasCompleted, setHasCompleted] = useState(() => {
    // Check backend preference first (from user object)
    if (user?.skip_onboarding_redirect) return true

    // Fallback to localStorage
    if (!userId) return false
    const completedKey = getUserStorageKey(COMPLETED_KEY_BASE, userId)
    const skippedKey = getUserStorageKey(SKIPPED_KEY_BASE, userId)
    return localStorage.getItem(completedKey) === 'true' ||
           localStorage.getItem(skippedKey) === 'true'
  })

  // Re-check when user changes
  useEffect(() => {
    if (isLoading) return

    // Check backend preference first (from user object)
    if (user?.skip_onboarding_redirect) {
      setHasCompleted(true)
      return
    }

    if (!userId) {
      setHasCompleted(false)
      return
    }

    const completedKey = getUserStorageKey(COMPLETED_KEY_BASE, userId)
    const skippedKey = getUserStorageKey(SKIPPED_KEY_BASE, userId)
    const completed = localStorage.getItem(completedKey) === 'true' ||
                     localStorage.getItem(skippedKey) === 'true'
    setHasCompleted(completed)
  }, [userId, isLoading, user?.skip_onboarding_redirect])

  useEffect(() => {
    if (!userId) return

    const checkCompleted = () => {
      const completedKey = getUserStorageKey(COMPLETED_KEY_BASE, userId)
      const skippedKey = getUserStorageKey(SKIPPED_KEY_BASE, userId)
      const completed = localStorage.getItem(completedKey) === 'true' ||
                       localStorage.getItem(skippedKey) === 'true'
      setHasCompleted(completed)
    }

    // Listen for storage changes (from other tabs)
    window.addEventListener('storage', checkCompleted)
    // Listen for custom event (from same tab)
    window.addEventListener('onboarding-status-changed', checkCompleted)
    return () => {
      window.removeEventListener('storage', checkCompleted)
      window.removeEventListener('onboarding-status-changed', checkCompleted)
    }
  }, [userId])

  return hasCompleted
}

// Export the helper for use in ChatModeProvider
export function getOnboardingStorageKeys(userId: string | null) {
  return {
    completedKey: getUserStorageKey(COMPLETED_KEY_BASE, userId),
    skippedKey: getUserStorageKey(SKIPPED_KEY_BASE, userId),
    stateKey: getUserStorageKey(STORAGE_KEY_BASE, userId),
  }
}

// Mark onboarding finished for a user, outside of any provider.
//
// Guided mode is not wrapped in OnboardingProvider, so it cannot use the
// context hook — but it still finishes onboarding by producing a trained
// model. Without this, OnboardingRedirect keeps bouncing the user back to
// /app/get-started from every /app route, forever.
export async function markOnboardingComplete(userId: string | null): Promise<void> {
  const { completedKey, stateKey } = getOnboardingStorageKeys(userId)
  localStorage.setItem(completedKey, 'true')
  localStorage.removeItem(stateKey)

  // Same-tab listeners only see the change via this event; `storage` fires
  // in other tabs only.
  window.dispatchEvent(new Event('onboarding-status-changed'))

  // Persist to backend so it syncs across devices
  try {
    await usersService.updatePreferences({ skip_onboarding_redirect: true })
  } catch (error) {
    console.error('Failed to persist onboarding completion to backend:', error)
    // Don't throw - localStorage is still set as fallback
  }
}

// Provider component
interface OnboardingProviderProps {
  children: ReactNode
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const onboardingValue = useOnboarding()

  return (
    <OnboardingContext.Provider value={onboardingValue}>
      {children}
    </OnboardingContext.Provider>
  )
}
